import { useCallback, useEffect, useRef, useState } from 'react';
import { find, retrieve } from '../loqate-client';
import normaliseAddress from '../normalise-address';
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_MS, SEARCH_STATUS } from '../constants';

const IDLE = { status: SEARCH_STATUS.IDLE, items: [], isStale: false };

// Failures fall back to manual entry without telling anyone but the developer.
const logInDevelopment = error => {
  if (process.env.NODE_ENV !== 'development') return;
  // eslint-disable-next-line no-console
  console.warn('[address-lookup]', error.code || error.name, error.message);
};

/**
 * Search state for one address field, limited to `country` (ISO2).
 *
 * - `search(text)` waits for MIN_SEARCH_LENGTH characters and a SEARCH_DEBOUNCE_MS pause, and
 *   always searches from the top: a container opened earlier is not kept. Until the new
 *   results arrive the current ones stay listed, so the list doesn't flicker as the customer
 *   types, but are marked `isStale` and must not be picked. Under MIN_SEARCH_LENGTH the
 *   results are cleared.
 * - `open(item)` lists the addresses inside a group result.
 * - `choose(item)` retrieves an address and resolves to its normalised form, or null.
 *
 * Starting any request aborts the one before it, so a slow response never replaces a
 * newer one, and Loqate sees as few Finds per completed address as possible.
 */
export default function useAddressSearch(country) {
  const [state, setState] = useState(IDLE);
  const timerRef = useRef(null);
  const controllerRef = useRef(null);
  const textRef = useRef('');

  const cancel = useCallback(() => {
    clearTimeout(timerRef.current);
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  // Resolves to the request's result, or null when it failed or was superseded.
  const run = useCallback(
    async request => {
      cancel();
      const controller = new AbortController();
      controllerRef.current = controller;
      setState(prev => ({ ...prev, status: SEARCH_STATUS.LOADING }));

      try {
        const result = await request(controller.signal);
        if (controller.signal.aborted) return null;
        controllerRef.current = null;
        return result;
      } catch (error) {
        if (controller.signal.aborted) return null;
        controllerRef.current = null;
        logInDevelopment(error);
        setState({ status: SEARCH_STATUS.ERROR, items: [], isStale: false });
        return null;
      }
    },
    [cancel]
  );

  const showResults = useCallback(items => {
    if (items) setState({ status: SEARCH_STATUS.DONE, items, isStale: false });
  }, []);

  const search = useCallback(
    text => {
      cancel();
      textRef.current = text;
      const trimmed = text.trim();
      if (trimmed.length < MIN_SEARCH_LENGTH) {
        setState(IDLE);
        return;
      }
      // Results for the previous text stay listed but can't be picked until the new ones land
      setState(prev => (prev.items.length ? { ...prev, isStale: true } : prev));

      timerRef.current = setTimeout(async () => {
        showResults(await run(signal => find({ text: trimmed, countries: country, signal })));
      }, SEARCH_DEBOUNCE_MS);
    },
    [cancel, run, showResults, country]
  );

  const open = useCallback(
    async item => {
      showResults(
        await run(signal =>
          find({ text: textRef.current.trim(), container: item.id, countries: country, signal })
        )
      );
    },
    [run, showResults, country]
  );

  const choose = useCallback(
    async item => {
      const address = await run(signal => retrieve(item.id, { signal }));
      if (!address) return null;
      setState(IDLE);
      return normaliseAddress(address);
    },
    [run]
  );

  const reset = useCallback(() => {
    cancel();
    textRef.current = '';
    setState(IDLE);
  }, [cancel]);

  return { ...state, search, open, choose, reset };
}
